import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, MonitorSmartphone, History, UserRound, LogOut, ChevronRight, ShieldUser } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';
import { checkMaintenance } from '../utils/checkMaintenance.js';

const NAV_CARDS = [
    {
        key: "apps",
        title: "Your Apps",
        desc: "Browse and open the apps linked to your account.",
        icon: LayoutGrid,
        to: "/apps",
    },
    {
        key: "devices",
        title: "Devices",
        desc: "See where you're signed in and revoke access.",
        icon: MonitorSmartphone,
        to: "/devices",
    },
    {
        key: "activity",
        title: "Login Activity",
        desc: "Review your recent sign-ins.",
        icon: History,
        to: "/login-activity",
    },
    {
        key: "profile",
        title: "Profile",
        desc: "Update your username, email, and avatar.",
        icon: UserRound,
        to: "/profile",
    },
];

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => {
        setAlerts(prev => ([...prev, { id: v4(), text }]));
    }

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await fetch(USER_URL + "/users", {
                    method: "GET",
                    credentials: "include",
                    headers: buildHeaders(),
                });
                const data = await res.json();
                
                if (!res.ok || data.failed) {
                    if (data.signout) navigate("/auth");
                    pushAlert(data.message || "Oops! something went wrong while loading your data.");
                    return;
                }

                setUser(data.data);
                checkMaintenance(data , navigate);
            } catch (err) {
                pushAlert("Oops! something went wrong while reaching the server.");
            } finally {
                setLoading(false);
            }
        }

        fetchUser();
    }, []);

    const handleLogout = () => {
        navigate("/auth");
    }

    // console.log(user);

    return (
        <div className="dashboard">
            <div className="top_side">
                <div className="greeting">
                    <span className="eyebrow">SME Account Center</span>
                    <h2>{loading ? "Welcome back" : `Welcome back, ${user?.username || "there"}`}</h2>
                    <p>Here's what's happening across your linked apps and devices.</p>
                </div>
                <button className="logout" onClick={handleLogout}>
                    Sign Out <LogOut size="18" />
                </button>
            </div>

            <div className="bottom_side">
                <div className="stats">
                    <div className="stat_card">
                        <span className="stat_value">{loading ? "—" : (user?.linkedApps?.length ?? 0)}</span>
                        <span className="stat_label">Linked Apps</span>
                    </div>
                    <div className="stat_card">
                        <span className="stat_value">{loading ? "—" : (user?.role || "user")}</span>
                        <span className="stat_label">Role</span>
                    </div>
                    <div className="stat_card">
                        <span className="stat_value">{loading ? "—" : (user?.accountStatus || "active")}</span>
                        <span className="stat_label">Account Status</span>
                    </div>
                </div>
                {user !== null && (user?.role === "admin" || user?.role === "viewer") ? <Link className="nav_card" to={"/admin"}>
                    <div className="nav_card_icon"><ShieldUser size={22}/></div>
                    <div className="nav_card_text">
                        <h3>Admin Hub</h3>
                        <p>Browse users and restrict areas and view or create new apps.</p>
                    </div>
                    <ChevronRight className="nav_card_arrow" size="20" />
                </Link> : undefined}
                <div className="nav_cards">
                    {NAV_CARDS.map(card => {
                        const Icon = card.icon;
                        return (
                            <Link className="nav_card" to={card.to} key={card.key}>
                                <div className="nav_card_icon"><Icon size="22" /></div>
                                <div className="nav_card_text">
                                    <h3>{card.title}</h3>
                                    <p>{card.desc}</p>
                                </div>
                                <ChevronRight className="nav_card_arrow" size="20" />
                            </Link>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default Dashboard;
